import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { api, ApiAuthError } from '../lib/api';
import { Badge, Button, Select, SearchableSelect } from '../atoms';
import { DataTable, CreateEntityModal, Modal, useToast, CustomFieldDefinitionModal, dataTypeLabel } from '../organisms';
import type { CustomFieldDataType, CustomFieldDefinition } from '../organisms';

/**
 * CustomFieldsAdmin — /custom-fields
 *
 * The per-client / per-property custom-field definition registry: the
 * anti-cookie-cutter control center. Pick a client (and optionally a property)
 * to see and manage the field schema scoped to it. A field defined for one
 * client is invisible to every other.
 *
 * Definition LISTING + edit + activate/deactivate ride the generic CRUD table
 * 'custom_field_definitions' (GET/PUT /api/custom-field-definitions), filtered
 * by client_id / property_id for the chosen scope and sorted by display_order.
 *
 * Definition LIFECYCLE (backlog 19.3-19.4) rides the dedicated endpoints, which
 * the generic CRUD surface can't express:
 *   POST /api/custom-fields/definitions          — create w/ server key-gen + validation
 *   PUT  /api/custom-fields/definitions/reorder   — atomic display_order reorder
 *   POST /api/custom-fields/definitions/ingest    — bulk-create from a spreadsheet
 *
 * Domain 19 — Per-Client Custom Fields. Gated to super_admin/admin.
 */

interface CurrentUser {
  role: string;
}

interface ClientRow {
  id: string;
  name: string;
}

interface PropertyRow {
  id: string;
  name: string;
}

interface IngestResult {
  created: CustomFieldDefinition[];
  skipped: Array<{ field_key: string; label: string; reason: string }>;
  columnCount: number;
}

export function CustomFieldsAdmin() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [clientId, setClientId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const userQuery = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
    retry: (failureCount, error) => {
      if (error instanceof ApiAuthError) return false;
      return failureCount < 2;
    },
  });

  // crud.js returns a { rows, total, page, limit } envelope when ?limit= is
  // present and clamps limit to <=200 — ask for the cap and unwrap defensively.
  const clientsQuery = useQuery<ClientRow[]>({
    queryKey: ['clients-for-custom-fields'],
    queryFn: async ({ signal }) => {
      const resp = await api.get<ClientRow[] | { rows: ClientRow[] }>('/api/clients?limit=200', signal);
      return Array.isArray(resp) ? resp : resp.rows;
    },
  });

  const propertiesQuery = useQuery<PropertyRow[]>({
    queryKey: ['properties-for-custom-fields', clientId],
    queryFn: async ({ signal }) => {
      const resp = await api.get<PropertyRow[] | { rows: PropertyRow[] }>(`/api/properties?client_id=${clientId}&limit=200`, signal);
      return Array.isArray(resp) ? resp : resp.rows;
    },
    enabled: !!clientId,
  });

  // Scope key: property overrides client. Only one filter is applied at a time
  // so "client-level" fields (property unselected) stay distinct from a
  // property's own fields.
  const scopeParam = propertyId ? `property_id=${propertyId}` : clientId ? `client_id=${clientId}` : '';
  const definitionsQuery = useQuery<CustomFieldDefinition[]>({
    queryKey: ['custom-field-definitions', clientId, propertyId],
    queryFn: ({ signal }) => api.get<CustomFieldDefinition[]>(`/api/custom-field-definitions?${scopeParam}&sort=display_order&dir=asc`, signal),
    enabled: !!scopeParam,
  });

  const invalidateDefs = () => {
    queryClient.invalidateQueries({ queryKey: ['custom-field-definitions', clientId, propertyId] });
    // ParcelsList's optional custom-field column picker reads a separate
    // ['parcel-custom-field-defs'] query — refresh it too so a newly
    // added/toggled definition shows up in the picker without a reload.
    queryClient.invalidateQueries({ queryKey: ['parcel-custom-field-defs'] });
  };

  const toggleActive = useMutation({
    mutationFn: (def: CustomFieldDefinition) =>
      api.put(`/api/custom-field-definitions/${def.id}`, { is_active: !def.is_active }),
    onSuccess: (_data, def) => {
      invalidateDefs();
      toast({ variant: 'success', message: `Field ${def.is_active ? 'deactivated' : 'reactivated'}.` });
    },
    onError: () => toast({ variant: 'error', message: 'Could not update the field.' }),
  });

  // Atomic reorder — the whole scope's id list in the new order (19.3).
  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.put('/api/custom-fields/definitions/reorder', { ordered_ids: orderedIds }),
    onSuccess: () => invalidateDefs(),
    onError: (err) => toast({ variant: 'error', message: err instanceof Error ? err.message : 'Reorder failed.' }),
  });

  function move(id: string, dir: -1 | 1) {
    const defs = definitionsQuery.data ?? [];
    const ids = defs.map(d => d.id);
    const idx = ids.indexOf(id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    reorder.mutate(ids);
  }

  if (userQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;
  }

  if (userQuery.data && userQuery.data.role !== 'super_admin' && userQuery.data.role !== 'admin' && userQuery.data.role !== 'administrator') {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>Custom field administration is restricted to super_admin and admin roles.</p>
      </div>
    );
  }

  // No empty-value entry: SearchableSelect carries the unselected state in its own
  // placeholder, so an "" option would just be an odd row in the search results.
  const clientOptions = (clientsQuery.data ?? []).map(c => ({ value: c.id, label: c.name }));
  const propertyOptions = [
    { value: '', label: 'All client-level fields' },
    ...(propertiesQuery.data ?? []).map(p => ({ value: p.id, label: p.name })),
  ];

  const scopeLabel = propertyId ? 'this property' : clientId ? 'this client' : '';
  const definitions = definitionsQuery.data ?? [];

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>Custom fields</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          Define the fields that make each client's records theirs. A field added for one client stays invisible to every other.
        </p>
      </header>

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--sp-3)',
        alignItems: 'flex-end',
        marginBottom: 'var(--sp-5)',
      }}>
        <FilterField label="Client">
          <SearchableSelect
            options={clientOptions}
            value={clientId || null}
            onChange={next => { setClientId(next ?? ''); setPropertyId(''); }}
            placeholder="Search clients…"
          />
        </FilterField>
        {clientId && (
          <FilterField label="Property scope">
            <Select
              options={propertyOptions}
              value={propertyId}
              onChange={e => setPropertyId(e.target.value)}
            />
          </FilterField>
        )}
        {clientId && (
          <>
            <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
              + Add field
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setImportOpen(true)}>
              Import from spreadsheet
            </Button>
          </>
        )}
      </div>

      {!clientId ? (
        <Empty message="Pick a client to see and manage its custom fields." />
      ) : (
        <DefinitionsTable
          definitions={definitions}
          loading={definitionsQuery.isLoading}
          error={definitionsQuery.error as Error | null}
          scopeLabel={scopeLabel}
          onEdit={(id) => setEditId(id)}
          onToggleActive={(def) => toggleActive.mutate(def)}
          onMove={move}
          busy={toggleActive.isPending || reorder.isPending}
        />
      )}

      <CustomFieldDefinitionModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        clientId={propertyId ? undefined : clientId}
        propertyId={propertyId || undefined}
        scopeLabel={scopeLabel}
        existingKeys={definitions.map(d => d.field_key)}
        onCreated={() => { invalidateDefs(); setCreateOpen(false); }}
      />

      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        clientId={clientId}
        propertyId={propertyId}
        scopeLabel={scopeLabel}
        onIngested={() => invalidateDefs()}
      />

      <CreateEntityModal
        open={!!editId}
        onClose={() => setEditId(null)}
        table="custom_field_definitions"
        title="Edit custom field"
        recordId={editId ?? undefined}
        invalidateKeys={[['custom-field-definitions', clientId, propertyId], ['parcel-custom-field-defs']]}
      />
    </>
  );
}

/**
 * BulkImportModal — upload a CSV/TSV and bulk-create a definition per header
 * column (19.4). Calls POST /api/custom-fields/definitions/ingest, which parses
 * the sheet with the frozen server parser, infers each column's type, and
 * creates the scoped definitions (idempotent — existing keys are skipped).
 */
function BulkImportModal({
  open, onClose, clientId, propertyId, scopeLabel, onIngested,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  propertyId: string;
  scopeLabel: string;
  onIngested: () => void;
}) {
  const { toast } = useToast();
  const [fileName, setFileName] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<IngestResult | null>(null);

  function reset() {
    setFileName(''); setSourceText(''); setError(null); setResult(null);
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    setError(null); setResult(null); setSourceText(''); setFileName('');
    if (!f) return;
    // Binary .xlsx is not supported by the shipped parser (text backend only).
    if (/\.xlsx?$/i.test(f.name)) {
      setError('Binary Excel (.xlsx) is not yet supported — export the sheet as CSV or TSV first.');
      return;
    }
    const text = await f.text();
    if (text.includes('\u0000') || text.startsWith('PK')) {
      setError('This looks like a binary file. Export the sheet as CSV or TSV first.');
      return;
    }
    setFileName(f.name);
    setSourceText(text);
  }

  async function submit() {
    if (!sourceText) return;
    const body: Record<string, unknown> = { source: { text: sourceText } };
    if (propertyId) body.property_id = propertyId; else body.client_id = clientId;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<IngestResult>('/api/custom-fields/definitions/ingest', body);
      setResult(res);
      onIngested();
      toast({
        variant: 'success',
        message: `${res.created.length} field${res.created.length === 1 ? '' : 's'} created for ${scopeLabel}.`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+: /, '') : 'Import failed.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Import fields from a spreadsheet"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={submitting}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result && (
            <Button variant="primary" onClick={submit} disabled={submitting || !sourceText}>
              {submitting ? 'Creating…' : 'Create fields'}
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
          Each column header in the file becomes a custom field for {scopeLabel}, with its type inferred
          from the data. Columns that already exist are skipped. Choose a .csv or .tsv file
          (binary .xlsx is not yet supported — export to CSV first).
        </p>
        <input type="file" accept=".csv,.tsv,.txt" onChange={onPickFile} style={{ fontFamily: 'inherit' }} />
        {fileName && !result && (
          <Badge variant="success" dot>{fileName}</Badge>
        )}
        {error && (
          <div style={{
            padding: 'var(--sp-3)', background: 'var(--color-status-red-bg)',
            border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
            borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)',
          }}>{error}</div>
        )}
        {result && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            <p style={{ margin: 0, color: 'var(--color-ink)' }}>
              <strong>{result.created.length}</strong> field{result.created.length === 1 ? '' : 's'} created
              {' '}· <strong>{result.skipped.length}</strong> skipped
              {' '}(of {result.columnCount} column{result.columnCount === 1 ? '' : 's'}).
            </p>
            {result.created.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                {result.created.map(d => (
                  <Badge key={d.id} variant="success">{d.label} · {dataTypeLabel[d.data_type]}</Badge>
                ))}
              </div>
            )}
            {result.skipped.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                {result.skipped.map((s, i) => (
                  <Badge key={`${s.field_key}-${i}`} variant="neutral">{s.label} · {s.reason}</Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function DefinitionsTable({
  definitions, loading, error, scopeLabel, onEdit, onToggleActive, onMove, busy,
}: {
  definitions: CustomFieldDefinition[];
  loading: boolean;
  error: Error | null;
  scopeLabel: string;
  onEdit: (id: string) => void;
  onToggleActive: (def: CustomFieldDefinition) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  busy: boolean;
}) {
  const columns = useMemo<ColumnDef<CustomFieldDefinition>[]>(() => [
    {
      id: 'order',
      header: 'Order',
      cell: ({ row, table }) => {
        const count = table.getRowModel().rows.length;
        const isFirst = row.index === 0;
        const isLast = row.index === count - 1;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <span style={{ color: 'var(--color-muted)', minWidth: 16 }}>{row.original.display_order}</span>
            <div style={{ display: 'flex', gap: 2 }}>
              <Button variant="ghost" size="sm" disabled={busy || isFirst} onClick={() => onMove(row.original.id, -1)} aria-label="Move up">↑</Button>
              <Button variant="ghost" size="sm" disabled={busy || isLast} onClick={() => onMove(row.original.id, 1)} aria-label="Move down">↓</Button>
            </div>
          </div>
        );
      },
    },
    { accessorKey: 'label', header: 'Label' },
    {
      accessorKey: 'field_key',
      header: 'Key',
      cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>{getValue() as string}</span>,
    },
    {
      accessorKey: 'data_type',
      header: 'Type',
      cell: ({ getValue }) => <Badge variant="neutral">{dataTypeLabel[getValue() as CustomFieldDataType]}</Badge>,
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ getValue }) => ((getValue() as boolean)
        ? <Badge variant="success" dot>Active</Badge>
        : <Badge variant="neutral" dot>Inactive</Badge>),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={() => onEdit(row.original.id)}>Edit</Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onToggleActive(row.original)}>
            {row.original.is_active ? 'Deactivate' : 'Reactivate'}
          </Button>
        </div>
      ),
    },
  ], [onEdit, onToggleActive, onMove, busy]);

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading fields…</p>;
  if (error) {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        Failed to load custom fields. {error.message}
      </div>
    );
  }
  if (definitions.length === 0) {
    return <Empty message={`No custom fields for ${scopeLabel} yet. Add the first one.`} />;
  }
  return <DataTable data={definitions} columns={columns} />;
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 220 }}>
      <span style={{
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</span>
      {children}
    </label>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
      fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}
